import React, { useReducer, useCallback } from 'react';
import OptionList from '../../../helpers/options/OptionList';
import useAccountTypeOptions from '../../../hooks/useAccountTypeOptions';
import useChartOfAccountTypeOptions from '../../../hooks/useChartOfAccountTypeOptions';
import classes from './EditPayableLineItems.module.css';
import Button from '../../../assets/essentials/Button';
import PutBody from '../../../api/internal/PutBody';

type LineItemProps = {
  payable: Payable;
  expenseCoaOptions: ChartOfAccount[];
  onUpdate: (payableId: number, field: string, value: string) => void;
};
const LineItem = ({ payable, expenseCoaOptions, onUpdate }: LineItemProps) => {
  const handleFieldChange = (field: string, value: string) => {
    onUpdate(payable.payableId, field, value);
  };

  return (
    <div className={classes.lineItem}>
      <div className={classes.fieldsGrid}>
        <div
          className={`${classes.fieldGroup} ${classes.accountField} ${classes.requiredField}`}
        >
          <label className={classes.fieldLabel}>Account</label>
          <select
            className={classes.fieldSelect}
            value={payable.chartOfAccountId}
            onChange={e =>
              handleFieldChange('chartOfAccountId', e.target.value)
            }
          >
            <OptionList
              optionData={expenseCoaOptions}
              attributeID="chartOfAccountId"
              attributeName="accountName"
              attributeGroup="group"
            />
          </select>
        </div>

        <div className={`${classes.fieldGroup} ${classes.quantityField}`}>
          <label className={classes.fieldLabel}>Quantity</label>
          <input
            type="number"
            className={classes.fieldInput}
            defaultValue={payable.quantity}
            min="0"
            step="0.01"
            placeholder="0.00"
            onChange={e => handleFieldChange('quantity', e.target.value)}
          />
        </div>

        <div className={`${classes.fieldGroup} ${classes.rateField}`}>
          <label className={classes.fieldLabel}>Rate</label>
          <input
            type="number"
            className={classes.fieldInput}
            defaultValue={payable.rate}
            min="0"
            step="0.01"
            placeholder="0.00"
            onChange={e => handleFieldChange('rate', e.target.value)}
          />
        </div>

        <div className={`${classes.fieldGroup} ${classes.totalField}`}>
          <label className={classes.fieldLabel}>Total</label>
          <input
            type="text"
            className={`${classes.fieldInput} ${classes.readOnlyField}`}
            value={(payable.rate * payable.quantity).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })}
            disabled
          />
        </div>
        <div className={`${classes.fieldGroup} ${classes.noteField}`}>
          <label className={classes.fieldLabel}>Note</label>
          <textarea
            className={classes.fieldTextarea}
            defaultValue={payable.note || ''}
            rows={2}
            onChange={e => handleFieldChange('note', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

const lineItemsReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return state.map(item =>
        item.payableId === action.payableId
          ? { ...item, [action.field]: action.value }
          : item,
      );
    case 'SET_LINE_ITEMS':
      return action.payload;
    default:
      return state;
  }
};

function EditPayableLineItems({
  lineItems: initialLineItems,
  vendorAssignmentId,
  onUpdate,
}) {
  const [lineItems, dispatch] = useReducer(lineItemsReducer, initialLineItems);
  const { chartOfAccountOptions } = useChartOfAccountTypeOptions({
    accountType: 'expense',
  });

  const handleLineItemUpdate = useCallback(
    (payableId: number, field: string, value: string) => {
      dispatch({
        type: 'UPDATE_FIELD',
        payableId,
        field,
        value,
      });
    },
    [],
  );

  const handleSave = useCallback(() => {
    const updatedLineItems = lineItems.map((payable: Payable) => ({
      payableId: payable.payableId,
      accountTypeId: payable.accountTypeId,
      chartOfAccountId: payable.chartOfAccountId,
      quantity: payable.quantity || 0,
      rate: payable.rate || 0,
      note: payable.note || '',
    }));

    PutBody('/Accounting/UpdateBillPayables', {
      vendorAssignmentId,
      payables: updatedLineItems,
    }).then(() => {
      onUpdate('Details', ['Summary', 'Audit']);
    });
  }, [lineItems, vendorAssignmentId, onUpdate]);

  if (!lineItems || lineItems.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          <div className={classes.emptyStateIcon}>📄</div>
          <div className={classes.emptyStateText}>No Line Items</div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.lineItemsWrapper}>
        {lineItems.map((payable: Payable) => (
          <LineItem
            key={payable.payableId}
            payable={payable}
            expenseCoaOptions={chartOfAccountOptions}
            onUpdate={handleLineItemUpdate}
          />
        ))}
      </div>
      <div>
        <Button type="button" variant="good" onClick={handleSave}>
          Update Line Items
        </Button>
      </div>
    </div>
  );
}

export default EditPayableLineItems;
